import { useMemo } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { Person } from '@/types/table.ts';
import { useTranslation } from '@/hooks/useTranslation.ts';

interface PersonTableProps {
  data: Person[];
  onEdit?: (person: Person) => void;
  onDelete?: (person: Person) => void;
  loading?: boolean;
}

export const PersonTable = ({
  data,
  onEdit,
  onDelete,
  loading,
}: PersonTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo<MRT_ColumnDef<Person>[]>(
    () => [
      {
        accessorKey: 'firstName',
        header: t('table.person.firstName'),
        size: 150,
        enableHiding: false,
      },
      {
        accessorKey: 'lastName',
        header: t('table.person.lastName'),
        size: 150,
        enableHiding: false,
      },
      {
        accessorKey: 'email',
        header: t('table.person.email'),
        size: 200,
        enableHiding: false,
      },
      {
        accessorKey: 'age',
        header: t('table.person.age'),
        size: 80,
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorKey: 'city',
        header: t('table.person.city'),
        size: 150,
      },
      {
        accessorKey: 'state',
        header: t('table.person.state'),
        size: 100,
      },
      {
        accessorKey: 'salary',
        header: t('table.person.salary'),
        size: 120,
        muiTableHeadCellProps: {
          align: 'right',
        },
        muiTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ cell }) => {
          const value = cell.getValue<number>();
          return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'RUB',
          }).format(value);
        },
      },
      {
        accessorKey: 'department',
        header: t('table.person.department'),
        size: 150,
      },
      {
        accessorKey: 'startDate',
        header: t('table.person.startDate'),
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return new Date(value).toLocaleDateString('ru-RU');
        },
      },
      {
        accessorKey: 'isActive',
        header: t('table.person.status'),
        size: 100,
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
        Cell: ({ cell }) => {
          const isActive = cell.getValue<boolean>();
          return (
            <Chip
              label={
                isActive ? t('table.person.active') : t('table.person.inactive')
              }
              color={isActive ? 'success' : 'default'}
              size="small"
            />
          );
        },
      },
    ],
    [t],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      isLoading: loading,
    },
    enableRowSelection: true,
    enableColumnOrdering: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableSorting: true,
    enableHiding: true,
    enableDensityToggle: true,
    enableFullScreenToggle: true,
    enablePagination: true,
    enableBottomToolbar: true,
    enableTopToolbar: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {onEdit && (
          <Tooltip title={t('table.actions.edit')}>
            <IconButton size="small" onClick={() => onEdit(row.original)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title={t('table.actions.delete')}>
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(row.original)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
      },
    },
    muiTableBodyRowProps: () => ({
      sx: {
        '&:nth-of-type(odd)': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      },
    }),
    paginationDisplayMode: 'pages',
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      variant: 'outlined',
    },
  });

  return <MaterialReactTable table={table} />;
};
